import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Check, X, Trash2, RefreshCcw, Plus, Image as ImageIcon, FileText, Calendar, Star, Settings, Home as HomeIcon, Upload, ChevronDown, ChevronRight, ChevronLeft, Search } from "lucide-react";
import { getAdminSupabase, signOut, getSession } from "../lib/auth";
import { getImageUrl } from "../lib/storage";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const STATUS_LABEL = { pending: "Pendente", confirmed: "Confirmada", cancelled: "Cancelada", completed: "Concluída" };
const STATUS_COLOR = { pending: "bg-amber-100 text-amber-800 border-amber-300", confirmed: "bg-emerald-100 text-emerald-800 border-emerald-300", cancelled: "bg-rose-100 text-rose-800 border-rose-300", completed: "bg-sky-100 text-sky-800 border-sky-300" };

function ConfirmDialog({ open, title, message, onConfirm, onCancel }) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title || "Confirmar"}</AlertDialogTitle>
          <AlertDialogDescription>{message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Confirmar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function PromptDialog({ open, title, value, onChange, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{title || "Introduzir valor"}</DialogTitle>
        </DialogHeader>
        <input className="ff-input w-full" value={value} onChange={(e) => onChange(e.target.value)} autoFocus />
        <DialogFooter>
          <button onClick={onCancel} className="px-4 py-2 rounded-full bg-slate-200 text-sm font-medium">Cancelar</button>
          <button onClick={onConfirm} className="ff-btn ff-btn-primary !py-2 !px-4 !text-sm">OK</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("reservations");
  const [stats, setStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [items, setItems] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [content, setContent] = useState(null);
  const [contentId, setContentId] = useState(null);
  const [loading, setLoading] = useState(true);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const confirmResolveRef = useRef(null);

  const [promptOpen, setPromptOpen] = useState(false);
  const [promptMsg, setPromptMsg] = useState("");
  const [promptValue, setPromptValue] = useState("");
  const promptResolveRef = useRef(null);

  const confirm = useCallback((msg) => {
    setConfirmMsg(msg);
    setConfirmOpen(true);
    return new Promise((resolve) => { confirmResolveRef.current = resolve; });
  }, []);

  const prompt = useCallback((msg, defaultValue = "") => {
    setPromptMsg(msg);
    setPromptValue(defaultValue);
    setPromptOpen(true);
    return new Promise((resolve) => { promptResolveRef.current = resolve; });
  }, []);

  const handleConfirmYes = useCallback(() => {
    setConfirmOpen(false);
    confirmResolveRef.current?.(true);
  }, []);

  const handleConfirmNo = useCallback(() => {
    setConfirmOpen(false);
    confirmResolveRef.current?.(false);
  }, []);

  const handlePromptYes = useCallback(() => {
    setPromptOpen(false);
    promptResolveRef.current?.(promptValue);
  }, [promptValue]);

  const handlePromptNo = useCallback(() => {
    setPromptOpen(false);
    promptResolveRef.current?.(null);
  }, []);

  const getDb = useCallback(async () => {
    const session = getSession();
    if (!session?.token) { navigate("/admin/login"); return null; }
    const db = getAdminSupabase();
    const { data: users } = await db.from("admin_users").select("id").eq("token", session.token).limit(1);
    if (!users || users.length === 0) { signOut(); navigate("/admin/login"); return null; }
    return db;
  }, [navigate]);

  const loadAll = async () => {
    try {
      const db = await getDb(); if (!db) return;
      const [s, r, i, g, rv, c] = await Promise.all([
        db.from("reservations").select("*", { count: "exact", head: true }),
        db.from("reservations").select("*").order("created_at", { ascending: false }),
        db.from("items").select("*").order("order"),
        db.from("gallery").select("*").order("order"),
        db.from("reviews").select("*").order("created_at", { ascending: false }),
        db.from("content").select("*").single(),
      ]);
      setStats({ total: s.count || 0, pending: (r.data||[]).filter(x=>x.status==="pending").length, confirmed: (r.data||[]).filter(x=>x.status==="confirmed").length, completed: (r.data||[]).filter(x=>x.status==="completed").length, reviews: rv.data?.length || 0 });
      setReservations(r.data || []); setItems(i.data || []); setGallery(g.data || []); setReviews(rv.data || []); setContent(c.data?.data || c.data); setContentId(c.data?.id || null);
    } catch (e) { console.error(e); toast.error("Erro ao carregar dados"); } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);
  const logout = async () => { await signOut(); navigate("/admin/login"); };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="title-pop text-2xl" style={{ color: "var(--ff-peach)" }}>A carregar...</div></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <ConfirmDialog open={confirmOpen} message={confirmMsg} onConfirm={handleConfirmYes} onCancel={handleConfirmNo} />
      <PromptDialog open={promptOpen} title={promptMsg} value={promptValue} onChange={setPromptValue} onConfirm={handlePromptYes} onCancel={handlePromptNo} />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <span className="font-display font-bold text-xl" style={{ color: "var(--ff-text)" }}>Fantastic Fun · Admin</span>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="hidden md:inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border border-slate-300 hover:bg-slate-100"><HomeIcon size={14} /> Ver site</a>
            <button onClick={loadAll} className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-full border border-slate-300 hover:bg-slate-100"><RefreshCcw size={14} /></button>
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full bg-rose-500 text-white hover:bg-rose-600"><LogOut size={14} /> Sair</button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        {stats && <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { l: "Total", v: stats.total, c: "bg-slate-100 text-slate-800" },
            { l: "Pendentes", v: stats.pending, c: "bg-amber-100 text-amber-800" },
            { l: "Confirmadas", v: stats.confirmed, c: "bg-emerald-100 text-emerald-800" },
            { l: "Concluídas", v: stats.completed, c: "bg-sky-100 text-sky-800" },
            { l: "Avaliações", v: stats.reviews, c: "bg-pink-100 text-pink-800" },
          ].map((s, i) => <div key={i} className={`admin-card p-4 ${s.c}`} data-testid={`stat-${s.l.toLowerCase()}`}><div className="text-xs uppercase tracking-wider font-semibold opacity-80">{s.l}</div><div className="text-3xl font-bold mt-1">{s.v}</div></div>)}
        </div>}
        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { key: "reservations", label: "Reservas", icon: Calendar },
            { key: "content", label: "Conteúdo", icon: FileText },
          ].map((t) => { const Icon = t.icon; const active = tab === t.key;
            return <button key={t.key} onClick={() => setTab(t.key)} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${active ? "bg-[var(--ff-peach)] text-white border-[var(--ff-peach)]" : "bg-white border-slate-300 hover:bg-slate-100"}`} data-testid={`admin-tab-${t.key}`}><Icon size={14} /> {t.label}</button>;})}
        </div>
        {tab === "reservations" && <ReservationsTab reservations={reservations} reload={loadAll} getDb={getDb} confirm={confirm} prompt={prompt} reviews={reviews} />}
        {tab === "content" && content && <ContentTab content={content} setContent={setContent} contentId={contentId} reload={loadAll} getDb={getDb} items={items} gallery={gallery} reviews={reviews} confirm={confirm} />}
      </div>
    </div>
  );
}

function ReservationsTab({ reservations, reload, getDb, confirm, prompt, reviews }) {
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const filtered = filter === "all" ? reservations : reservations.filter((r) => r.status === filter);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);
  const [sendingReview, setSendingReview] = useState(null);

  const updateStatus = async (id, status) => {
    let updateData = { status };
    if (status === "cancelled") {
      const reason = await prompt("Motivo (opcional):");
      if (reason === null) return;
      if (reason) updateData.reason = reason;
    }
    try { const db = await getDb(); if (!db) return; await db.from("reservations").update(updateData).eq("id", id); toast.success(status === "confirmed" ? "Reserva confirmada" : "Estado atualizado"); reload(); } catch (e) { console.error(e); toast.error("Erro ao atualizar"); }
  };
  const remove = async (id) => {
    const ok = await confirm("Eliminar esta reserva?"); if (!ok) return;
    try { const db = await getDb(); if (!db) return; await db.from("reservations").delete().eq("id", id); toast.success("Reserva eliminada"); reload(); } catch (e) { console.error(e); toast.error("Erro ao eliminar"); }
  };
  const sendReview = async (r) => {
    setSendingReview(r.id);
    try {
      const emailRes = await fetch("/api/send-review-email", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: r.name, email: r.email, item_name: r.item_name }),
      });
      if (!emailRes.ok) throw new Error("Erro ao enviar");
      toast.success("Pedido de avaliação enviado!");
    } catch (e) {
      toast.error("Erro ao enviar pedido de avaliação");
    } finally { setSendingReview(null); }
  };

  const avatarColors = { pending: "from-amber-200 to-amber-300 text-amber-700", confirmed: "from-emerald-200 to-emerald-300 text-emerald-700", completed: "from-sky-200 to-sky-300 text-sky-700", cancelled: "from-rose-200 to-rose-300 text-rose-700" };

  return (
    <div className="bg-white rounded-2xl shadow-md border border-slate-100 overflow-hidden">
      <div className="p-5 md:p-6 border-b border-slate-100 bg-gradient-to-r from-[var(--ff-peach)]/5 via-white to-[var(--ff-sky)]/5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-800">Reservas</h2>
            <p className="text-sm text-slate-500 mt-0.5">{filtered.length} {filtered.length === 1 ? 'reserva' : 'reservas'} encontrada{filtered.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {["all","pending","confirmed","completed","cancelled"].map((f) => {
              const count = f === "all" ? reservations.length : reservations.filter(r => r.status === f).length;
              const Icon = f === "all" ? Calendar : f === "pending" ? Calendar : f === "cancelled" ? X : Check;
              const activeColors = f === "all" ? "bg-slate-900 text-white border-slate-900" : f === "pending" ? "bg-amber-500 text-white border-amber-500" : f === "confirmed" ? "bg-emerald-500 text-white border-emerald-500" : f === "completed" ? "bg-sky-500 text-white border-sky-500" : "bg-rose-500 text-white border-rose-500";
              return (
                <button key={f} onClick={() => { setFilter(f); setPage(1); }} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-medium transition-all ${filter===f ? activeColors + " shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`} data-testid={`filter-${f}`}>
                  <Icon size={12} />
                  {f==="all" ? "Todas" : STATUS_LABEL[f]}
                  <span className={`${filter===f ? "text-white/60" : "text-slate-400"}`}>{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left font-semibold text-slate-600 py-3.5 px-5">Cliente</th>
              <th className="text-left font-semibold text-slate-600 py-3.5 px-5 hidden sm:table-cell">Item</th>
              <th className="text-left font-semibold text-slate-600 py-3.5 px-5">Data</th>
              <th className="text-left font-semibold text-slate-600 py-3.5 px-5 hidden lg:table-cell">Local</th>
              <th className="text-left font-semibold text-slate-600 py-3.5 px-5">Estado</th>
              <th className="text-right font-semibold text-slate-600 py-3.5 px-5 w-72">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paged.length===0 && (
              <tr><td colSpan={6} className="py-20 text-center">
                <div className="flex flex-col items-center gap-2"><Calendar size={32} className="text-slate-300" /><p className="text-slate-400 font-medium">Nenhuma reserva encontrada</p></div>
              </td></tr>
            )}
            {paged.map((r) => (
              <tr key={r.id} className={`hover:bg-slate-50/80 transition-colors ${r.status === "cancelled" ? "opacity-70" : ""}`} data-testid={`reservation-row-${r.id}`}>
                <td className="py-3.5 px-5">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${avatarColors[r.status]} flex items-center justify-center font-bold text-sm shrink-0 shadow-sm`}>
                      {r.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">{r.name}</div>
                      <div className="text-xs text-slate-400">{r.email}</div>
                      <div className="text-xs text-slate-400 md:hidden">{r.phone} · {r.item_name}</div>
                    </div>
                  </div>
                </td>
                <td className="py-3.5 px-5 text-slate-700 hidden sm:table-cell">
                  <span className="text-sm font-medium text-slate-700">{r.item_name}</span>
                  <div className="text-xs text-slate-400">{r.phone}</div>
                </td>
                <td className="py-3.5 px-5">
                  <span className="text-slate-700">{r.event_date}</span>
                </td>
                <td className="py-3.5 px-5 text-slate-700 hidden lg:table-cell">
                  <span className="inline-flex items-center gap-1"><span className="text-slate-400">📍</span> {r.location}</span>
                </td>
                <td className="py-3.5 px-5">
                  <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-medium shadow-sm ${STATUS_COLOR[r.status]}`}>
                    {r.status === "pending" && <Calendar size={12} />}
                    {r.status === "confirmed" && <Check size={12} />}
                    {r.status === "completed" && <Check size={12} />}
                    {r.status === "cancelled" && <X size={12} />}
                    {STATUS_LABEL[r.status]}
                  </span>
                </td>
                <td className="py-3.5 px-5 text-right">
                  <div className="inline-flex gap-2 items-center justify-end flex-wrap">
                    {r.status==="pending" && (
                      <>
                        <button onClick={async () => { const ok = await confirm("Confirmar esta reserva?"); if (ok) await updateStatus(r.id,"confirmed"); }} className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 font-medium shadow-sm transition-all" data-testid={`confirm-${r.id}`}><Check size={14} /> Confirmar</button>
                        <button onClick={async () => { const ok = await confirm("Cancelar esta reserva?"); if (ok) await updateStatus(r.id,"cancelled"); }} className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-lg bg-rose-500 text-white hover:bg-rose-600 font-medium shadow-sm transition-all" data-testid={`cancel-${r.id}`}><X size={14} /> Cancelar</button>
                      </>
                    )}
                    {r.status==="confirmed" && (
                      <button onClick={async () => { const ok = await confirm("Concluir esta reserva?"); if (ok) await updateStatus(r.id,"completed"); }} className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 font-medium shadow-sm transition-all"><Check size={14} /> Concluir</button>
                    )}
                    {(() => {
                      if (r.status !== "completed") return null;
                      const norm = (s) => (s||"").toString().trim().toLowerCase();
                      const match = reviews.find(rv => norm(rv.customer_name) === norm(r.name) && norm(rv.item_name) === norm(r.item_name) && norm(rv.event_date) === norm(r.event_date));
                      if (match) return (
                        <button onClick={async () => {
                          const ok = await confirm("Eliminar avaliação deste cliente?");
                          if (!ok) return;
                          const db = await getDb(); if(!db) return;
                          await db.from("reviews").delete().eq("id", match.id);
                          toast.success("Avaliação eliminada");
                          reload();
                        }} className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-lg bg-rose-500 text-white hover:bg-rose-600 font-medium shadow-sm transition-all"><X size={14} /> Eliminar Avaliação</button>
                      );
                      return (
                        <button onClick={async () => { const ok = await confirm("Solicitar avaliação a este cliente?"); if (ok) sendReview(r); }} disabled={sendingReview === r.id} className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 font-medium shadow-sm transition-all disabled:opacity-50"><Star size={14} /> {sendingReview === r.id ? "A enviar..." : "Enviar Avaliação"}</button>
                      );
                    })()}
                    <button onClick={async () => { const ok = await confirm("Eliminar esta reserva?"); if (ok) await remove(r.id); }} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg bg-white text-slate-500 border border-slate-200 hover:bg-slate-100 hover:text-rose-600 font-medium transition-all shadow-sm" data-testid={`delete-${r.id}`}><Trash2 size={14} /> Eliminar</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-5 py-4 border-t border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <p className="text-xs text-slate-500"><span className="font-medium text-slate-700">{(page-1)*perPage+1}</span><span className="text-slate-300">–</span><span className="font-medium text-slate-700">{Math.min(page*perPage, filtered.length)}</span> de <span className="font-medium text-slate-700">{filtered.length}</span></p>
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-400">Por página:</span>
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }} className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-[var(--ff-peach)] cursor-pointer">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-100 bg-white shadow-sm"><ChevronLeft size={15} className="text-slate-600" /></button>
              {Array.from({length: totalPages}, (_, i) => i + 1).slice(Math.max(0, page-3), Math.min(totalPages, page+2)).map((p) => (
                <button key={p} onClick={() => setPage(p)} className={`min-w-[32px] h-8 rounded-lg text-xs font-medium transition-all ${p === page ? "bg-slate-900 text-white shadow-sm" : "border border-slate-200 text-slate-600 hover:bg-slate-100 bg-white"}`}>{p}</button>
              ))}
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-30 hover:bg-slate-100 bg-white shadow-sm"><ChevronRight size={15} className="text-slate-600" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContentTab({ content, setContent, contentId, reload, getDb, items, gallery, reviews, confirm }) {
  const [savingSection, setSavingSection] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [itemEditing, setItemEditing] = useState(null);
  const [galleryEditing, setGalleryEditing] = useState(null);
  const sf = (path, value) => { setContent((c) => { const n={...c}; const k=path.split("."); let r=n; for(let i=0;i<k.length-1;i++){r[k[i]]={...(r[k[i]] || {})};r=r[k[i]];}r[k[k.length-1]]=value;return n;}); };
  const saveContent = async (section) => { setSavingSection(section); try { const db=await getDb(); if(!db)return; const id = contentId || "00000000-0000-0000-0000-000000000001"; await db.from("content").update({data:content}).eq("id",id); toast.success(`${section} guardado`); reload(); } catch(e){console.error(e); toast.error("Erro ao guardar");} finally{setSavingSection(null);} };
  const saveAll = async () => { setSavingSection("all"); try { const db=await getDb(); if(!db)return; const id = contentId || "00000000-0000-0000-0000-000000000001"; await db.from("content").update({data:content}).eq("id",id); toast.success("Todo o conteúdo guardado"); reload(); } catch(e){console.error(e); toast.error("Erro ao guardar");} finally{setSavingSection(null);} };
  const gv = (p) => p.split(".").reduce((o,k)=>o?.[k],content)||"";
  
  const itemBlank = { slug:"", name:"", short_description:"", description:"", price_text:"", image_url:"", color:"#F5B0B0", order:99, active:true };
  const saveItem = async (it, isNew) => { try { const db=await getDb(); if(!db)return; const { _new, ...data } = it; if(isNew) await db.from("items").insert(data); else await db.from("items").update(data).eq("slug",data.slug); toast.success("Item guardado"); setItemEditing(null); reload(); } catch(e){console.error(e); toast.error(e?.message||"Erro");} };
  const removeItem = async (slug) => { const ok = await confirm("Eliminar item?"); if (!ok) return; const db=await getDb(); if(!db)return; await db.from("items").delete().eq("slug",slug); toast.success("Eliminado"); reload(); };
  
  const galleryBlank = { image_url: "", caption: "", order: (gallery[gallery.length-1]?.order||0)+1 };
  const saveGallery = async (g, isNew) => { try { const db=await getDb(); if(!db)return; const { _new, ...data } = g; if(isNew) await db.from("gallery").insert(data); else await db.from("gallery").update(data).eq("id",data.id); toast.success("Imagem guardada"); setGalleryEditing(null); reload(); } catch(e){console.error(e); toast.error(e?.message||"Erro");} };
  const removeGallery = async (id) => { const ok = await confirm("Eliminar imagem?"); if (!ok) return; const db=await getDb(); if(!db)return; await db.from("gallery").delete().eq("id",id); toast.success("Eliminada"); reload(); };

  const toggleReviewPub = async (rv) => { const db=await getDb(); if(!db)return; await db.from("reviews").update({published:!rv.published}).eq("id",rv.id); toast.success("Atualizado"); reload(); };

  const sections = [
    { title: "Marca", subtitle: "Nome, tagline, redes", icon: Settings, color: "bg-rose-100 text-rose-600", type: "brand" },
    { title: "Hero", subtitle: "Título, imagem principal", icon: ImageIcon, color: "bg-purple-100 text-purple-600", type: "hero" },
    { title: "Itens", subtitle: `${items.length} itens + gestionar`, icon: Settings, color: "bg-emerald-100 text-emerald-600", type: "items" },
    { title: "Galeria", subtitle: `${gallery.length} imágenes + gestionar`, icon: ImageIcon, color: "bg-amber-100 text-amber-600", type: "gallery" },
    { title: "Depoimentos", subtitle: `${reviews.length} avaliações`, icon: Star, color: "bg-pink-100 text-pink-600", type: "reviews" },
    { title: "Sobre", subtitle: "Texto, história", icon: FileText, color: "bg-blue-100 text-blue-600", type: "about" },
    { title: "Formulário", subtitle: "Título, descrição", icon: FileText, color: "bg-cyan-100 text-cyan-600", type: "form" },
    { title: "Footer", subtitle: "Direitos, tagline", icon: Settings, color: "bg-slate-100 text-slate-600", type: "footer" },
  ];
  
  const currentSection = sections[currentStep];
  const isSaving = savingSection !== null;

  const renderBrandFields = () => (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Nome</label>
        <input className="ff-input" value={gv("brand.name")} onChange={(e)=>sf("brand.name",e.target.value)} placeholder="Nome da marca" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Tagline</label>
        <input className="ff-input" value={gv("brand.tagline")} onChange={(e)=>sf("brand.tagline",e.target.value)} placeholder="Tagline" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Instagram</label>
        <input className="ff-input" value={gv("brand.instagram")} onChange={(e)=>sf("brand.instagram",e.target.value)} placeholder="@instagram" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Email</label>
        <input className="ff-input" value={gv("brand.email")} onChange={(e)=>sf("brand.email",e.target.value)} placeholder="email@exemplo.com" />
      </div>
    </div>
  );

  const renderHeroFields = () => (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Settings size={14} className="text-slate-400" /> Eyebrow</label>
        <input className="ff-input" value={gv("hero.eyebrow")} onChange={(e)=>sf("hero.eyebrow",e.target.value)} placeholder="Eyebrow" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Settings size={14} className="text-slate-400" /> Título Topo</label>
        <input className="ff-input" value={gv("hero.title_top")} onChange={(e)=>sf("hero.title_top",e.target.value)} placeholder="Ex: Festas Incríveis" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Settings size={14} className="text-slate-400" /> Título Base</label>
        <input className="ff-input" value={gv("hero.title_bottom")} onChange={(e)=>sf("hero.title_bottom",e.target.value)} placeholder="Ex: Para o seu filho" />
      </div>
      <div className="space-y-2 md:col-span-2">
        <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><FileText size={14} className="text-slate-400" /> Subtítulo</label>
        <textarea className="ff-input min-h-[80px] resize-none" value={gv("hero.subtitle")} onChange={(e)=>sf("hero.subtitle",e.target.value)} placeholder="Subtítulo descritivo" />
      </div>
      <div className="md:col-span-2">
        <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-3"><ImageIcon size={14} className="text-slate-400" /> Imagens do Hero</label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {[
            { key: "hero.hero_image", label: "Imagem Principal", frame: "frame-scallop" },
            { key: "hero.side_image", label: "Imagem Lateral", frame: "frame-scallop frame-scallop-blue" },
          ].map(({ key, label, frame }) => {
            const val = gv(key);
            return (
              <div key={key} className={`${frame} w-full mx-auto`}>
                <div className="frame-inner aspect-[4/3] relative group overflow-hidden">
                  {val ? (
                    <>
                      <img src={val} alt={label} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-3">
                        <div className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all"><ImageUploader folder="content" onUpload={(url)=>sf(key,url)} /></div>
                        <button onClick={async () => { const ok = await confirm("Eliminar esta imagem?"); if (ok) sf(key,""); }} className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all p-2 bg-rose-500 text-white rounded-full hover:bg-rose-600 shadow-xl"><X size={14} /></button>
                      </div>
                    </>
                  ) : (
                    <ImageUploader folder="content" onUpload={(url)=>sf(key,url)} big />
                  )}
                  <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 text-white text-[10px] rounded-md font-medium">{label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderItemsSection = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-slate-50 to-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center gap-2 text-slate-700">
          <Settings size={16} />
          <h4 className="font-semibold">Configuração da Seção</h4>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Eyebrow</label>
            <input className="ff-input" value={gv("items_section.eyebrow")} onChange={(e)=>sf("items_section.eyebrow",e.target.value)} placeholder="Eyebrow" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Título</label>
            <input className="ff-input" value={gv("items_section.title")} onChange={(e)=>sf("items_section.title",e.target.value)} placeholder="Título" />
          </div>
        </div>
      </div>
      <div className="border-t border-slate-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-slate-800 flex items-center gap-2"><Plus size={16} className="text-slate-500" /> Gerir Itens</h4>
            <p className="text-sm text-slate-500">{items.length} {items.length === 1 ? 'item' : 'itens'} cadastrado{items.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={()=>setItemEditing({...itemBlank,_new:true})} className="ff-btn ff-btn-primary !py-2 !px-4 !text-sm"><Plus size={16} className="mr-1"/>Novo Item</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {items.length === 0 && <div className="md:col-span-3 text-slate-400 text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">Sem itens cadastrados ainda</div>}
          {items.map((it, idx)=>(
            <article key={it.slug} className="group flex flex-col items-center text-center relative">
              <div className={`frame-scallop w-full ${idx % 2 === 1 ? "frame-scallop-blue" : ""}`} style={idx === 2 ? { background: it.color } : {}}>
                <div className="frame-inner aspect-square relative overflow-hidden">
                  <ImgPreview src={it.image_url} alt={it.name} />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-3">
                    <button onClick={()=>setItemEditing(it)} className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all text-xs px-3 py-2 rounded-lg bg-white text-slate-900 font-medium hover:bg-slate-100 shadow-lg">Editar</button>
                    <button onClick={()=>removeItem(it.slug)} className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all text-xs px-3 py-2 rounded-lg bg-rose-500 text-white font-medium hover:bg-rose-600 shadow-lg">Eliminar</button>
                  </div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <h3 className="font-bold text-slate-800">{it.name}</h3>
                <p className="text-xs text-slate-400">{it.slug} · ordem {it.order}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
      {itemEditing && <ItemEditor item={itemEditing} onClose={()=>setItemEditing(null)} onSave={saveItem} confirm={confirm} />}
    </div>
  );

  const renderGallerySection = () => (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-slate-50 to-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <div className="flex items-center gap-2 text-slate-700">
          <ImageIcon size={16} />
          <h4 className="font-semibold">Configuração da Seção</h4>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Eyebrow</label>
            <input className="ff-input" value={gv("gallery_section.eyebrow")} onChange={(e)=>sf("gallery_section.eyebrow",e.target.value)} placeholder="Eyebrow" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Título</label>
            <input className="ff-input" value={gv("gallery_section.title")} onChange={(e)=>sf("gallery_section.title",e.target.value)} placeholder="Título" />
          </div>
        </div>
      </div>
      <div className="border-t border-slate-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="font-semibold text-slate-800 flex items-center gap-2"><ImageIcon size={16} className="text-slate-500" /> Gerir Galeria</h4>
            <p className="text-sm text-slate-500">{gallery.length} {gallery.length === 1 ? 'imagem' : 'imagens'} na galeria</p>
          </div>
          <button onClick={()=>setGalleryEditing({...galleryBlank,_new:true})} className="ff-btn ff-btn-primary !py-2 !px-4 !text-sm"><Plus size={16} className="mr-1"/>Nova Imagem</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {gallery.length === 0 && <div className="md:col-span-3 text-slate-400 text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">Sem imagens na galeria ainda</div>}
          {gallery.map((g, idx)=>(
            <div key={g.id} className="group">
              <div className="relative">
                <div className="absolute top-2 left-2 z-10 bg-white/90 backdrop-blur-sm rounded-full w-7 h-7 flex items-center justify-center font-bold text-xs shadow-lg" style={{ color: "var(--ff-peach)" }}>
                  {g.order}
                </div>
                <div className="rounded-2xl overflow-hidden border-[4px] aspect-[4/5]" style={{ borderColor: idx % 2 === 0 ? "var(--ff-peach)" : "var(--ff-sky)" }}>
                  <ImgPreview src={g.image_url} alt={g.caption} />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors rounded-2xl flex items-center justify-center gap-3" style={{ margin: "4px" }}>
                  <button onClick={()=>setGalleryEditing(g)} className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all text-xs px-3 py-2 rounded-lg bg-white text-slate-900 font-medium hover:bg-slate-100 shadow-lg">Editar</button>
                  <button onClick={()=>removeGallery(g.id)} className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all text-xs px-3 py-2 rounded-lg bg-rose-500 text-white font-medium hover:bg-rose-600 shadow-lg">Eliminar</button>
                </div>
              </div>
              <div className="bg-white rounded-xl p-3 mt-2 shadow-sm border border-slate-100">
                <p className="text-center font-semibold text-sm text-slate-800 truncate">{g.caption || <span className="text-slate-400 font-normal italic">Sem legenda</span>}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {galleryEditing && <GalleryEditor item={galleryEditing} onClose={()=>setGalleryEditing(null)} onSave={saveGallery} confirm={confirm} />}
    </div>
  );

  const [reviewSearch, setReviewSearch] = useState("");
  const [reviewFilter, setReviewFilter] = useState("all");
  const [reviewPage, setReviewPage] = useState(1);
  const [reviewPerPage, setReviewPerPage] = useState(10);
  const filteredReviews = reviews.filter((rv) => {
    if (reviewFilter === "published" && !rv.published) return false;
    if (reviewFilter === "hidden" && rv.published) return false;
    if (reviewSearch && !rv.customer_name.toLowerCase().includes(reviewSearch.toLowerCase()) && !(rv.item_name||"").toLowerCase().includes(reviewSearch.toLowerCase())) return false;
    return true;
  });
  const PER_PAGE = reviewPerPage;
  const totalPages = Math.max(1, Math.ceil(filteredReviews.length / PER_PAGE));
  const paged = filteredReviews.slice((reviewPage - 1) * PER_PAGE, reviewPage * PER_PAGE);

  const renderReviewsSection = () => (
    <div>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { key: "all", label: "Todas", count: reviews.length },
            { key: "published", label: "Publicadas", count: reviews.filter(r => r.published).length },
            { key: "hidden", label: "Ocultas", count: reviews.filter(r => !r.published).length },
          ].map((f) => (
            <button key={f.key} onClick={() => { setReviewFilter(f.key); setReviewPage(1); }} className={`text-xs px-3.5 py-1.5 rounded-full border font-medium transition-all ${reviewFilter === f.key ? "bg-[var(--ff-peach)] text-white border-[var(--ff-peach)]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {f.label} <span className={`ml-1 ${reviewFilter === f.key ? "text-white/70" : "text-slate-400"}`}>({f.count})</span>
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Buscar cliente ou item..." value={reviewSearch} onChange={(e) => { setReviewSearch(e.target.value); setReviewPage(1); }} className="ff-input !py-1.5 !text-xs pl-9 w-56" />
        </div>
      </div>
      {filteredReviews.length === 0 ? (
        <div className="text-slate-400 text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">Nenhuma avaliação encontrada</div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left font-semibold text-slate-600 px-4 py-3 w-10"></th>
                  <th className="text-left font-semibold text-slate-600 px-4 py-3">Cliente</th>
                  <th className="text-left font-semibold text-slate-600 px-4 py-3 hidden md:table-cell">Item</th>
                  <th className="text-center font-semibold text-slate-600 px-4 py-3 w-24">Rating</th>
                  <th className="text-left font-semibold text-slate-600 px-4 py-3 hidden lg:table-cell">Comentário</th>
                  <th className="text-center font-semibold text-slate-600 px-4 py-3 w-24">Estado</th>
                  <th className="text-right font-semibold text-slate-600 px-4 py-3 w-48">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paged.map((rv) => (
                  <tr key={rv.id} className={`hover:bg-slate-50 transition-colors ${!rv.published ? "bg-slate-50/50" : ""}`}>
                    <td className="px-4 py-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--ff-peach)] to-purple-400 flex items-center justify-center text-white font-bold text-xs">
                        {rv.customer_name.charAt(0).toUpperCase()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-800">{rv.customer_name}</div>
                      <div className="text-xs text-slate-400">{rv.event_date}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-xs text-slate-500">{rv.item_name || <span className="italic text-slate-300">—</span>}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        {[1,2,3,4,5].map((n) => (
                          <Star key={n} size={12} fill={n <= rv.rating ? "#F59E0B" : "none"} color={n <= rv.rating ? "#F59E0B" : "#e2e8f0"} strokeWidth={1.5} />
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <p className="text-xs text-slate-600 truncate max-w-[200px]">{rv.comment || <span className="italic text-slate-300">Sem comentário</span>}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border font-medium shadow-sm ${
                        rv.published 
                          ? "bg-emerald-100 text-emerald-700 border-emerald-300" 
                          : "bg-slate-100 text-slate-500 border-slate-200"
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${rv.published ? "bg-emerald-500" : "bg-slate-400"}`} />
                        {rv.published ? "Publicado" : "Oculto"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button onClick={async () => { const ok = await confirm(rv.published ? "Ocultar esta avaliação?" : "Publicar esta avaliação?"); if (ok) toggleReviewPub(rv); }} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all shadow-sm ${
                          rv.published 
                            ? "bg-amber-400 text-white hover:bg-amber-500" 
                            : "bg-emerald-500 text-white hover:bg-emerald-600"
                        }`}>
                          {rv.published ? <X size={12} /> : <Check size={12} />}
                          {rv.published ? "Ocultar" : "Publicar"}
                        </button>
                        <button onClick={async () => { const ok = await confirm("Eliminar esta avaliação?"); if (ok) { const db = await getDb(); if(!db) return; await db.from("reviews").delete().eq("id", rv.id); toast.success("Avaliação eliminada"); reload(); } }} className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium bg-white text-rose-500 border border-rose-200 hover:bg-rose-50 transition-all shadow-sm">
                          <Trash2 size={12} /> Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between gap-4 flex-wrap mt-4">
            <div className="flex items-center gap-3">
              <p className="text-xs text-slate-500">{(reviewPage-1)*PER_PAGE+1}-{Math.min(reviewPage*PER_PAGE, filteredReviews.length)} de {filteredReviews.length}</p>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-400">Por página:</span>
                <select value={reviewPerPage} onChange={(e) => { setReviewPerPage(Number(e.target.value)); setReviewPage(1); }} className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-600 focus:outline-none focus:ring-1 focus:ring-[var(--ff-peach)]">
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button onClick={() => setReviewPage(Math.max(1, reviewPage - 1))} disabled={reviewPage === 1} className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft size={14} className="text-slate-600" /></button>
                {Array.from({length: totalPages}, (_, i) => i + 1).slice(Math.max(0, reviewPage-3), Math.min(totalPages, reviewPage+2)).map((p) => (
                  <button key={p} onClick={() => setReviewPage(p)} className={`w-8 h-8 rounded-lg text-xs font-medium ${p === reviewPage ? "bg-[var(--ff-peach)] text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{p}</button>
                ))}
                <button onClick={() => setReviewPage(Math.min(totalPages, reviewPage + 1))} disabled={reviewPage === totalPages} className="p-1.5 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight size={14} className="text-slate-600" /></button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderAboutFields = () => (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Eyebrow</label>
        <input className="ff-input" value={gv("about.eyebrow")} onChange={(e)=>sf("about.eyebrow",e.target.value)} placeholder="Eyebrow" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Título</label>
        <input className="ff-input" value={gv("about.title")} onChange={(e)=>sf("about.title",e.target.value)} placeholder="Título" />
      </div>
      <div className="space-y-2 md:col-span-2">
        <label className="text-sm font-semibold text-slate-700">Texto</label>
        <textarea className="ff-input min-h-[150px] resize-none" value={gv("about.body")} onChange={(e)=>sf("about.body",e.target.value)} placeholder="Texto sobre a empresa" />
      </div>
    </div>
  );

  const renderFormFields = () => (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Eyebrow</label>
        <input className="ff-input" value={gv("form_section.eyebrow")} onChange={(e)=>sf("form_section.eyebrow",e.target.value)} placeholder="Eyebrow" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Título</label>
        <input className="ff-input" value={gv("form_section.title")} onChange={(e)=>sf("form_section.title",e.target.value)} placeholder="Título" />
      </div>
      <div className="space-y-2 md:col-span-2">
        <label className="text-sm font-semibold text-slate-700">Subtítulo</label>
        <textarea className="ff-input min-h-[80px] resize-none" value={gv("form_section.subtitle")} onChange={(e)=>sf("form_section.subtitle",e.target.value)} placeholder="Subtítulo" />
      </div>
    </div>
  );

  const renderFooterFields = () => (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Tagline</label>
        <input className="ff-input" value={gv("footer.tagline")} onChange={(e)=>sf("footer.tagline",e.target.value)} placeholder="Tagline" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-slate-700">Direitos</label>
        <input className="ff-input" value={gv("footer.rights")} onChange={(e)=>sf("footer.rights",e.target.value)} placeholder="© 2024 ..." />
      </div>
    </div>
  );

  const renderContent = () => {
    switch(currentSection.type) {
      case "brand": return renderBrandFields();
      case "hero": return renderHeroFields();
      case "items": return renderItemsSection();
      case "gallery": return renderGallerySection();
      case "reviews": return renderReviewsSection();
      case "about": return renderAboutFields();
      case "form": return renderFormFields();
      case "footer": return renderFooterFields();
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[var(--ff-peach)] to-[var(--ff-peach)]/80 rounded-3xl p-6 md:p-8 shadow-lg">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-3xl font-bold text-white mb-2">Conteúdo do Site</h2>
            <p className="text-white/80">Passo {currentStep + 1} de {sections.length}: <span className="font-semibold text-white">{currentSection.title}</span></p>
          </div>
          <div className="flex items-center gap-2">
            {sections.map((_, idx) => (
              <div key={idx} className={`w-2.5 h-2.5 rounded-full transition-all ${idx === currentStep ? "bg-white scale-125" : idx < currentStep ? "bg-white/60" : "bg-white/25"}`} />
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-72 flex-shrink-0">
          <div className="bg-white rounded-2xl shadow-md border border-slate-100 overflow-hidden">
            <div className="bg-slate-50 px-4 py-2.5 border-b border-slate-100">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Navegação</span>
            </div>
            <div className="p-2 space-y-0.5">
              {sections.map((sec, idx) => {
                const Icon = sec.icon;
                const isActive = idx === currentStep;
                return (
                  <button
                    key={sec.title}
                    onClick={() => setCurrentStep(idx)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-200 ${
                      isActive 
                        ? "bg-[var(--ff-peach)] text-white shadow-md" 
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <div className={`p-1.5 rounded-lg ${isActive ? "bg-white/20" : "bg-slate-100"}`}>
                      <Icon size={14} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{sec.title}</div>
                      <div className={`text-xs truncate ${isActive ? "text-white/70" : "text-slate-400"}`}>{sec.subtitle}</div>
                    </div>
                    {isActive && <div className="w-1 h-8 bg-white/40 rounded-full" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="bg-white rounded-2xl shadow-md border border-slate-100 overflow-hidden">
            <div className={`px-6 py-5 ${currentSection.color}`}>
              <div className="flex items-center gap-3 mb-1">
                <div className={`p-2.5 rounded-xl bg-white shadow-sm ${currentSection.color.split(" ")[0]}`}>
                  <currentSection.icon size={22} />
                </div>
                <div>
                  <h3 className="font-display text-2xl font-bold text-slate-800">{currentSection.title}</h3>
                  <p className="text-sm text-slate-500 mt-0.5">{
                    currentSection.type === "brand" ? "Defina o nome, tagline e redes sociais da sua marca" :
                    currentSection.type === "hero" ? "Configure título, subtítulo e imagens de destaque" :
                    currentSection.type === "items" ? "Edite os itens do catálogo e a configuração da seção" :
                    currentSection.type === "gallery" ? "Faça upload de imagens e configure a galeria" :
                    currentSection.type === "reviews" ? "Veja e gerencie as avaliações dos clientes" :
                    currentSection.type === "about" ? "Conte a história da sua empresa" :
                    currentSection.type === "form" ? "Personalize o texto do formulário de reservas" :
                    "Ajuste a tagline e informações de direitos autorais"
                  }</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              {renderContent()}
            </div>
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
              <button
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
                className="inline-flex items-center gap-1.5 ff-btn !py-2.5 !px-5 bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <ChevronLeft size={16} /> Anterior
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => saveContent(currentSection.title)}
                  disabled={isSaving}
                  className="inline-flex items-center gap-1.5 ff-btn !py-2.5 !px-5 bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100"
                >
                  {isSaving ? "A guardar..." : <><Check size={16} /> Guardar</>}
                </button>
                {currentStep < sections.length - 1 ? (
                  <button
                    onClick={() => setCurrentStep(currentStep + 1)}
                    className="inline-flex items-center gap-1.5 ff-btn ff-btn-primary !py-2.5 !px-5"
                  >
                    Próximo <ChevronRight size={16} />
                  </button>
                ) : (
                  <button
                    onClick={saveAll}
                    disabled={isSaving}
                    className="inline-flex items-center gap-1.5 ff-btn !py-2.5 !px-5 bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm"
                  >
                    {isSaving ? "A guardar..." : <><Check size={16} /> Concluir</>}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ItemsTab({ items, reload, getDb, confirm }) {
  const [editing, setEditing] = useState(null);
  const blank = { slug:"", name:"", short_description:"", description:"", price_text:"", image_url:"", color:"#F5B0B0", order:99, active:true };
  const save = async (it, isNew) => { try { const db=await getDb(); if(!db)return; const { _new, ...data } = it; if(isNew) await db.from("items").insert(data); else await db.from("items").update(data).eq("slug",data.slug); toast.success("Item guardado"); setEditing(null); reload(); } catch(e){console.error(e); toast.error(e?.message||"Erro");} };
  const remove = async (slug) => { const ok = await confirm("Eliminar item?"); if (!ok) return; const db=await getDb(); if(!db)return; await db.from("items").delete().eq("slug",slug); toast.success("Eliminado"); reload(); };
  return (
    <div className="admin-card p-4 md:p-6">
      <div className="flex items-center justify-between mb-4"><h2 className="font-display text-2xl font-bold">Itens</h2><button onClick={()=>setEditing({...blank,_new:true})} className="ff-btn ff-btn-primary !py-2 !px-5 !text-sm"><Plus size={16} className="mr-1"/>Novo</button></div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{items.map((it)=><div key={it.slug} className="border border-slate-200 rounded-2xl p-4"><div className="aspect-video rounded-xl overflow-hidden mb-3 bg-slate-100"><ImgPreview src={it.image_url} alt={it.name} /></div><div className="font-bold">{it.name}</div><div className="text-xs text-slate-500 mb-3">{it.slug} · ordem {it.order}</div><div className="flex gap-2"><button onClick={()=>setEditing(it)} className="text-xs px-3 py-1.5 rounded-lg bg-slate-900 text-white">Editar</button><button onClick={()=>remove(it.slug)} className="text-xs px-3 py-1.5 rounded-lg bg-rose-100 text-rose-700">Eliminar</button></div></div>)}</div>
      {editing && <ItemEditor item={editing} onClose={()=>setEditing(null)} onSave={save}/>}
    </div>
  );
}

const COLOR_OPTIONS = [
  { name: "Rosa", value: "#F5B0B0" },
  { name: "Azul", value: "#A8C5E2" },
  { name: "Verde", value: "#B8E0C8" },
  { name: "Amarelo", value: "#F5E6A3" },
  { name: "Laranja", value: "#F5D0A3" },
  { name: "Roxo", value: "#D4B8E2" },
  { name: "Vermelho", value: "#E2A8A8" },
  { name: "Ciano", value: "#A8E2E2" },
  { name: "Cinza", value: "#C8C8C8" },
  { name: "Branco", value: "#FFFFFF" },
];

function ColorPicker({ value, onChange }) {
  const [customOpen, setCustomOpen] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {COLOR_OPTIONS.map((c) => (
          <button
            key={c.value}
            type="button"
            title={c.name}
            onClick={() => onChange(c.value)}
            className={`w-8 h-8 rounded-full border-2 transition-all ${value === c.value ? 'border-slate-900 scale-110' : 'border-transparent hover:scale-105'}`}
            style={{ backgroundColor: c.value }}
          />
        ))}
        <button
          type="button"
          onClick={() => setCustomOpen(!customOpen)}
          className="w-8 h-8 rounded-full border-2 border-dashed border-slate-400 flex items-center justify-center text-slate-500 hover:border-slate-600"
        >
          +
        </button>
      </div>
      {customOpen && (
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={value || "#F5B0B0"}
            onChange={(e) => onChange(e.target.value)}
            className="w-10 h-10 rounded cursor-pointer"
          />
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#F5B0B0"
            className="ff-input flex-1 !py-1.5"
          />
        </div>
      )}
    </div>
  );
}

function ItemEditor({ item, onClose, onSave, confirm }) {
  const [form,setForm]=useState(item); const isNew=!!item._new; const set=(k,v)=>setForm((f)=>({...f,[k]:v}));
  const [showSlugEdit, setShowSlugEdit] = useState(false);
  
  const generateSlug = (name) => {
    if (!name) return "";
    return name.toLowerCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (value) => {
    set("name", value);
    if (isNew || !form.slug) {
      set("slug", generateSlug(value));
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-5 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-[var(--ff-peach)] to-purple-400 shadow-sm">
                {isNew ? <Plus size={20} className="text-white" /> : <Settings size={20} className="text-white" />}
              </div>
              <div>
                <h3 className="font-display text-xl font-bold text-slate-800">{isNew ? "Novo Item" : "Editar Item"}</h3>
                <p className="text-sm text-slate-500">{isNew ? "Adicione um novo item ao catálogo" : "Atualize as informações do item"}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-200 transition-colors">
              <X size={20} className="text-slate-500" />
            </button>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-5">
              <div>
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5"><Settings size={14} className="text-slate-400" /> Nome do Item</label>
                <input 
                  className="ff-input text-lg font-bold" 
                  value={form.name} 
                  onChange={(e)=>handleNameChange(e.target.value)} 
                  placeholder="Ex: Castelo Gonflável"
                />
                {form.slug && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="text-xs text-slate-400">slug: {form.slug}</span>
                    <button onClick={() => setShowSlugEdit(!showSlugEdit)} className="text-xs text-[var(--ff-peach)] hover:underline">{showSlugEdit ? "Fechar" : "Editar"}</button>
                  </div>
                )}
                {showSlugEdit && (
                  <input className="ff-input !py-1.5 !text-xs mt-1.5" value={form.slug} onChange={(e)=>set("slug",generateSlug(e.target.value))} placeholder="slug-do-item" />
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5"> Preço</label>
                  <div className="relative">
                    <input 
                      className="ff-input pl-7" 
                      value={form.price_text} 
                      onChange={(e)=>set("price_text",e.target.value)} 
                      placeholder="€150/hora"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5"> Ordem</label>
                  <input 
                    type="number" 
                    className="ff-input" 
                    value={form.order} 
                    onChange={(e)=>set("order",parseInt(e.target.value||"0"))}
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5"> Descrição Curta</label>
                <input 
                  className="ff-input" 
                  value={form.short_description} 
                  onChange={(e)=>set("short_description",e.target.value)}
                  placeholder="Uma frase de destaque"
                />
              </div>
              
              <div>
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5"> Descrição Completa</label>
                <textarea 
                  className="ff-input min-h-[120px] resize-none" 
                  value={form.description} 
                  onChange={(e)=>set("description",e.target.value)}
                  placeholder="Descrição detalhada do item..."
                />
              </div>
            </div>
            
            <div className="space-y-5">
              <div>
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5 mb-1.5"><ImageIcon size={14} className="text-slate-400" /> Imagem</label>
                <div className="frame-scallop w-full" style={{ "--frame-color": form.color || "#F5B0B0" }}>
                  <div className="frame-inner aspect-square relative overflow-hidden bg-slate-50 group">
                    {form.image_url ? (
                      <>
                        <img src={form.image_url} alt={form.name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-3">
                          <div className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all"><ImageUploader folder="items" onUpload={(url)=>set("image_url",url)} /></div>
                          <button onClick={async () => { const ok = await confirm("Eliminar esta imagem?"); if (ok) set("image_url",""); }} className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all p-2 bg-rose-500 text-white rounded-full hover:bg-rose-600 shadow-xl"><X size={14} /></button>
                        </div>
                      </>
                    ) : (
                      <ImageUploader folder="items" onUpload={(url)=>set("image_url",url)} big />
                    )}
                  </div>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
                <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"> Cor do Card</label>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl border-2 border-slate-200 shadow-sm" style={{ backgroundColor: form.color || "#F5B0B0" }} />
                  <div className="flex-1">
                    <div className="text-sm font-mono text-slate-600">{form.color || "#F5B0B0"}</div>
                    <div className="text-xs text-slate-400">Cor da borda decorativa</div>
                  </div>
                </div>
                <ColorPicker value={form.color || "#F5B0B0"} onChange={(c) => set("color", c)} />
              </div>
              
              <button type="button" onClick={() => set("active", !form.active)} className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl cursor-pointer hover:bg-slate-100 transition-colors w-full text-left">
                <div className={`w-11 h-6 rounded-full transition-colors relative ${form.active ? "bg-emerald-400" : "bg-slate-300"}`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 left-0.5 transition-all ${form.active ? "translate-x-[22px]" : "translate-x-0"}`} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-700">Item Ativo</div>
                  <div className="text-xs text-slate-400">{form.active ? "Visível no site" : "Oculto do site"}</div>
                </div>
              </button>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full bg-white border border-slate-200 font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors text-sm">
            Cancelar
          </button>
          <button onClick={()=>onSave(form,isNew)} className="ff-btn ff-btn-primary !py-2.5 !px-6 !text-sm shadow-sm">
            {isNew ? "Criar Item" : "Guardar Alterações"}
          </button>
        </div>
      </div>
    </div>
  );
}

function GalleryTab({ gallery, reload, getDb, confirm }) {
  const [editing, setEditing] = useState(null);
  const blank = { image_url: "", caption: "", order: (gallery[gallery.length-1]?.order||0)+1 };
  const save = async (g, isNew) => { try { const db=await getDb(); if(!db)return; const { _new, ...data } = g; if(isNew) await db.from("gallery").insert(data); else await db.from("gallery").update(data).eq("id",data.id); toast.success("Imagem guardada"); setEditing(null); reload(); } catch(e){console.error(e); toast.error(e?.message||"Erro");} };
  const remove = async (id) => { const ok = await confirm("Eliminar imagem?"); if (!ok) return; const db=await getDb(); if(!db)return; await db.from("gallery").delete().eq("id",id); toast.success("Eliminada"); reload(); };
  return (
    <div className="admin-card p-4 md:p-6">
      <div className="flex items-center justify-between mb-4"><h2 className="font-display text-2xl font-bold">Galeria</h2><button onClick={()=>setEditing({...blank,_new:true})} className="ff-btn ff-btn-primary !py-2 !px-5 !text-sm"><Plus size={16} className="mr-1"/>Novo</button></div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{gallery.length===0&&<div className="md:col-span-3 text-slate-400 text-center py-8">Sem imagens na galeria</div>}{gallery.map((g)=><div key={g.id} className="border border-slate-200 rounded-2xl p-4"><div className="aspect-video rounded-xl overflow-hidden mb-3 bg-slate-100"><ImgPreview src={g.image_url} alt={g.caption} /></div><div className="font-bold truncate">{g.caption||<em className="text-slate-400 not-italic">Sem legenda</em>}</div><div className="text-xs text-slate-500 mb-3">ordem {g.order}</div><div className="flex gap-2"><button onClick={()=>setEditing(g)} className="text-xs px-3 py-1.5 rounded-lg bg-slate-900 text-white">Editar</button><button onClick={()=>remove(g.id)} className="text-xs px-3 py-1.5 rounded-lg bg-rose-100 text-rose-700">Eliminar</button></div></div>)}</div>
      {editing && <GalleryEditor item={editing} onClose={()=>setEditing(null)} onSave={save}/>}
    </div>
  );
}

function GalleryEditor({ item, onClose, onSave, confirm }) {
  const [form,setForm]=useState(item); const isNew=!!item._new; const set=(k,v)=>setForm((f)=>({...f,[k]:v}));
  const borderColor = form.order % 2 === 0 ? "var(--ff-peach)" : "var(--ff-sky)";
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden flex flex-col shadow-2xl">
        <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-5 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-[var(--ff-peach)] to-[var(--ff-sky)] shadow-sm">
                {isNew ? <Plus size={20} className="text-white" /> : <ImageIcon size={20} className="text-white" />}
              </div>
              <div>
                <h3 className="font-display text-xl font-bold text-slate-800">{isNew ? "Nova Imagem" : "Editar Imagem"}</h3>
                <p className="text-sm text-slate-500">{isNew ? "Adicione uma imagem à galeria" : "Atualize a imagem"}</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-200 transition-colors">
              <X size={20} className="text-slate-500" />
            </button>
          </div>
        </div>
        
        <div className="p-6 space-y-5">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><ImageIcon size={14} className="text-slate-400" /> Imagem</label>
              <div style={{ maxWidth: 280, margin: "0 auto" }}>
                <div className="relative">
                  <div className="absolute top-2 left-2 z-10 bg-white/90 backdrop-blur-sm rounded-full w-7 h-7 flex items-center justify-center font-bold text-xs shadow-lg" style={{ color: "var(--ff-peach)" }}>
                    {form.order}
                  </div>
                  <div className="rounded-2xl overflow-hidden border-[4px] aspect-[4/5] bg-slate-50 group relative" style={{ borderColor }}>
                    {form.image_url ? (
                      <>
                        <img src={form.image_url} alt={form.caption} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-colors flex items-center justify-center gap-3">
                          <div className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all"><ImageUploader folder="gallery" onUpload={(url)=>set("image_url",url)} /></div>
                          <button onClick={async () => { const ok = await confirm("Eliminar esta imagem?"); if (ok) set("image_url",""); }} className="translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all p-2 bg-rose-500 text-white rounded-full hover:bg-rose-600 shadow-xl"><X size={14} /></button>
                        </div>
                      </>
                    ) : (
                      <ImageUploader folder="gallery" onUpload={(url)=>set("image_url",url)} big />
                    )}
                  </div>
                </div>
              </div>
            </div>
          
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><FileText size={14} className="text-slate-400" /> Legenda</label>
            <input 
              className="ff-input" 
              value={form.caption} 
              onChange={(e)=>set("caption",e.target.value)}
              placeholder="Texto descritivo (opcional)"
            />
          </div>
          
          <div className="flex items-end gap-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"> Ordem</label>
              <input 
                type="number" 
                className="ff-input w-24" 
                value={form.order} 
                onChange={(e)=>set("order",parseInt(e.target.value||"0"))}
              />
            </div>
            <p className="text-xs text-slate-400 pb-2">Número menor aparece primeiro</p>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full bg-white border border-slate-200 font-medium text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors text-sm">
            Cancelar
          </button>
          <button onClick={()=>onSave(form,isNew)} className="ff-btn ff-btn-primary !py-2.5 !px-6 !text-sm shadow-sm">
            {isNew ? "Adicionar" : "Guardar Alterações"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImgPreview({ src, alt }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return <div className="w-full h-full bg-slate-100 flex items-center justify-center"><ImageIcon size={24} className="text-slate-300"/></div>;
  return <img src={src} alt={alt || "preview"} className="w-full h-full object-cover" onError={() => setFailed(true)} />;
}

function ImageUploader({ folder, onUpload, big }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const BUCKET = process.env.REACT_APP_SUPABASE_STORAGE_BUCKET || "images";
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${folder}/${Date.now()}.${ext}`;
      const db = getAdminSupabase();
      let { data: buckets } = await db.storage.listBuckets();
      if (!buckets?.find((b) => b.name === BUCKET)) {
        await db.storage.createBucket(BUCKET, { public: true });
      }
      const { error } = await db.storage.from(BUCKET).upload(path, file, { upsert: true, contentType: file.type, cacheControl: "3600" });
      if (error) throw error;
      const { data } = db.storage.from(BUCKET).getPublicUrl(path);
      onUpload(data.publicUrl);
      toast.success("Upload feito!");
    } catch (e) {
      console.error(e);
      toast.error("Erro ao fazer upload");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  if (big) {
    return (
      <>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="w-full h-full min-h-[200px] flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer">
          {uploading ? (
            <div className="flex flex-col items-center gap-2"><RefreshCcw size={24} className="text-slate-400 animate-spin" /><span className="text-sm text-slate-500">A enviar...</span></div>
          ) : (
            <div className="flex flex-col items-center gap-2"><div className="p-4 rounded-full bg-slate-100"><Upload size={28} className="text-slate-400" /></div><span className="text-sm font-semibold text-slate-600">Clique para fazer upload</span></div>
          )}
        </button>
      </>
    );
  }
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="text-xs px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1 mt-1">
        <Upload size={12} /> {uploading ? "A enviar..." : "Upload"}
      </button>
    </>
  );
}

function ReviewsTab({ reviews, reload, getDb }) {
  const togglePub = async (rv) => { const db=await getDb(); if(!db)return; await db.from("reviews").update({published:!rv.published}).eq("id",rv.id); toast.success("Atualizado"); reload(); };
  return (
    <div className="admin-card p-4 md:p-6">
      <h2 className="font-display text-2xl font-bold mb-4">Avaliações</h2>
      <div className="space-y-3">{reviews.length===0&&<div className="text-slate-400 text-center py-8">Sem avaliações ainda</div>}{reviews.map((rv)=><div key={rv.id} className="border border-slate-200 rounded-2xl p-4"><div className="flex items-start justify-between flex-wrap gap-2"><div><div className="font-bold">{rv.customer_name}</div><div className="text-amber-500 mb-1">{"★".repeat(rv.rating)}{"☆".repeat(5-rv.rating)}</div><p className="text-sm text-slate-700">{rv.comment||<em className="text-slate-400">Sem comentário</em>}</p></div><button onClick={()=>togglePub(rv)} className={`text-xs px-3 py-1.5 rounded-full border ${rv.published?"bg-emerald-100 text-emerald-700 border-emerald-300":"bg-slate-100 text-slate-600 border-slate-300"}`}>{rv.published?"Publicado":"Oculto"}</button></div></div>)}</div>
    </div>
  );
}
